/**
 * Change photo — a fixed circular crop over a pannable, zoomable stage. Pass 15 rev B, screen 02.
 *
 * THE MASK IS THE MARK. The circle on the stage is the same shape the league sees the picture in,
 * so what he frames is what everybody gets; there is no "it looked fine in the picker" gap. The
 * output goes through the same pipeline a plain upload always used — `encodeCrop` in
 * `avatarImage.ts` — so the 96px, ≤60KB result the rules insist on is unchanged.
 *
 * NOTHING IS WRITTEN UNTIL SAVE, REMOVE INCLUDED. Remove used to clear the picture the moment it
 * was pressed and leave Save greyed, so the only way out was Cancel, which then "saved" the
 * no-photo state. Tim, 4 September: "remove photo shouldn't actually remove my photo until I click
 * Save." So Remove stages the removal: the stage shows the initials, a line says what Save will
 * do, Undo puts the picture back on the stage, and Cancel leaves the league's copy untouched. The
 * original file is never kept anywhere; the square the person framed is what the league stores.
 *
 * A dialog on a desktop, a sheet on a phone, through the same `dock` as every other sheet.
 */

import { useCallback, useEffect, useRef, useState, type PointerEvent as RPointerEvent } from 'react';
import { decodeImage, encodeCrop, type Decoded } from './avatarImage.js';
import { GmBadge } from './GmBadge.js';
import { dock } from './dock.js';
import { note } from './auditor.js';
import type { LeagueStore } from '../store/types.js';

/** The visible stage, in CSS px. The mask is a circle of `MASK` inside it. */
const STAGE = 320;
const MASK = 220;

export function PhotoDialog(
  { gm, actor, store, current, onClose }: {
    gm: string; actor: string; store: LeagueStore;
    /** The picture the league sees now, or null. Shown on the stage until a new one is chosen. */
    current: string | null;
    onClose: () => void;
  },
) {
  const [bitmap, setBitmap] = useState<(CanvasImageSource & Decoded) | null>(null);
  const [url, setUrl] = useState<string | null>(current);
  /** Offset of the image's centre from the stage's centre, in stage px, at zoom 1. */
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /** Remove has been pressed and not undone; Save will clear the picture. */
  const [removing, setRemoving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  const drag = useRef<{ x: number; y: number; px: number; py: number } | null>(null);

  /* Decode whatever is on the stage so Save has pixels to crop, including the current picture. */
  useEffect(() => {
    if (!url) { setBitmap(null); return; }
    let live = true;
    void (async () => {
      try {
        const res = await fetch(url);
        const blob = await res.blob();
        const b = await decodeImage(blob);
        if (live) setBitmap(b);
      } catch {
        if (live) setBitmap(null);
      }
    })();
    return () => { live = false; };
  }, [url]);

  /** Scale that makes the image's shorter side exactly fill the mask at zoom 1. */
  const fit = bitmap ? MASK / Math.min(bitmap.width, bitmap.height) : 1;
  const scale = fit * zoom;

  /** Keep the mask covered: the image may not slide so far that a gap opens inside the circle. */
  const clamp = useCallback((p: { x: number; y: number }, z = zoom) => {
    if (!bitmap) return p;
    const s = fit * z;
    const w = bitmap.width * s;
    const h = bitmap.height * s;
    const mx = Math.max(0, (w - MASK) / 2);
    const my = Math.max(0, (h - MASK) / 2);
    return { x: Math.min(mx, Math.max(-mx, p.x)), y: Math.min(my, Math.max(-my, p.y)) };
  }, [bitmap, fit, zoom]);

  const onDown = (e: RPointerEvent) => {
    if (!bitmap) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y };
  };
  const onMove = (e: RPointerEvent) => {
    if (!drag.current) return;
    const d = drag.current;
    setPan(clamp({ x: d.px + (e.clientX - d.x), y: d.py + (e.clientY - d.y) }));
    setDirty(true);
  };
  const onUp = () => { drag.current = null; };

  async function choose(f: File) {
    setErr(null);
    try {
      await decodeImage(f); // validates before the stage swaps
      setRemoving(false);
      setUrl(URL.createObjectURL(f));
      setPan({ x: 0, y: 0 });
      setZoom(1);
      setDirty(true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'That picture would not open.');
    } finally {
      if (file.current) file.current.value = '';
    }
  }

  /** The crop in source pixels: the mask's square, mapped back through pan and zoom. */
  function cropNow() {
    if (!bitmap) return null;
    const size = MASK / scale;
    const cx = bitmap.width / 2 - pan.x / scale;
    const cy = bitmap.height / 2 - pan.y / scale;
    return { sx: Math.round(cx - size / 2), sy: Math.round(cy - size / 2), size: Math.round(size) };
  }

  async function save() {
    if (removing) {
      setBusy(true);
      setErr(null);
      try {
        note(store, actor, { kind: 'delete', what: 'Removed his picture', target: `avatars/${gm}` });
        await store.clearAvatar(gm);
        onClose();
      } catch {
        setErr('That did not save. Try again.');
      } finally {
        setBusy(false);
      }
      return;
    }
    if (!bitmap) return;
    const crop = cropNow();
    if (!crop) return;
    setBusy(true);
    setErr(null);
    try {
      const image = encodeCrop(bitmap, crop);
      await store.saveAvatar({
        gm: gm.toUpperCase(),
        image,
        updatedAt: new Date().toISOString(),
        /* Whoever actually chose it, which is not always the man in the picture. */
        updatedBy: actor.toUpperCase(),
      });
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'That picture would not upload.');
    } finally {
      setBusy(false);
    }
  }

  /** Stage the removal. Nothing is written; Save does that. */
  function remove() {
    if (!current) return;
    setErr(null);
    setRemoving(true);
    setUrl(null);
    setDirty(true);
  }

  /** Put the league's picture back on the stage. Still nothing written. */
  function undo() {
    setRemoving(false);
    setUrl(current);
    setPan({ x: 0, y: 0 });
    setZoom(1);
    setDirty(false);
  }

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) void choose(f);
  };

  return dock(
    <>
      <button type="button" className="sheetscrim kdscrim" aria-label="Close" onClick={onClose} />
      <div className="kdsheet photodlg" role="dialog" aria-modal="true" aria-label="Change photo">
        <div className="photohead">
          <h2 className="kdsheeth">Change photo</h2>
          <button type="button" className="photox" aria-label="Close" onClick={onClose}>×</button>
        </div>

        <div className="stage" style={{ width: STAGE, height: STAGE }}
          onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
          onDragOver={(e) => e.preventDefault()} onDrop={onDrop}>
          {url && bitmap ? (
            <img src={url} alt="" draggable={false}
              style={{
                width: bitmap.width * scale, height: bitmap.height * scale,
                transform: `translate(${pan.x}px, ${pan.y}px)`,
              }} />
          ) : (
            <GmBadge gm={gm} size="md" className="stagemark" />
          )}
          <div className="mask" style={{ width: MASK, height: MASK }} aria-hidden="true" />
          {url && bitmap && <span className="stagehint">Drag to reposition</span>}
        </div>

        <label className="zoomrow">
          <span className="peyebrow">Zoom</span>
          <input type="range" min={1} max={3} step={0.01} value={zoom} disabled={!bitmap}
            onChange={(e) => { const z = Number(e.target.value); setZoom(z); setPan((p) => clamp(p, z)); setDirty(true); }} />
        </label>

        <button type="button" className="dropzone" onClick={() => file.current?.click()}>
          Drop a new photo, or <b>browse</b>
        </button>
        <input ref={file} type="file" accept="image/*" hidden
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void choose(f); }} />

        {err && <p className="estaxbad">{err}</p>}
        {removing && (
          <p className="fact removedline">
            Save removes your photo; the league sees your initials.{' '}
            <button type="button" className="linkbtn" onClick={undo}>Undo</button>
          </p>
        )}

        <div className="kdsheetacts photoacts">
          {current && !removing && (
            <button type="button" className="linkbtn danger" disabled={busy} onClick={remove}>
              Remove photo
            </button>
          )}
          <span className="spacer" />
          <button type="button" className="kdkeep" onClick={onClose}>Cancel</button>
          <button type="button" className="kdsubmit" disabled={busy || !dirty || (!removing && !bitmap)}
            onClick={() => void save()}>
            {busy ? 'Saving…' : removing ? 'Save without a photo' : 'Save'}
          </button>
        </div>
      </div>
    </>,
  );
}
